import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { CreditCard, QrCode, AlertTriangle } from 'lucide-react';
import { CourseBasics, PaymentCapabilities } from './types';

type PaymentFields = Pick<CourseBasics, 'price' | 'stripeEnabled' | 'qrPaymentEnabled' | 'paymentProvider'>;

interface CoursePaymentMethodsProps {
  value: PaymentFields;
  onChange: (patch: Partial<CourseBasics>) => void;
  capabilities: PaymentCapabilities;
}

const PAYMENT_CONFIG_PATH = '/my-teaching/stripe-config';
const checkboxCls = 'w-4 h-4 rounded border-gray-300 text-gold focus:ring-gold';
const radioCls = 'w-4 h-4 text-gold focus:ring-gold';

const OWN_STRIPE_STATUS_KEY = {
  missing: 'courseEdit.ownStripeMissing',
  disabled: 'courseEdit.ownStripeDisabled',
  unreadable: 'courseEdit.ownStripeUnreadable',
} as const;

/**
 * Per-course choice of how students pay. Two independent options, both may be on:
 * - Stripe card checkout, through either the platform account (Default) or the trainer's own
 *   ("My Stripe" — never falls back to the platform; no card payment while it isn't ready)
 * - Customized payment: the trainer's QR / instruction methods shown in a popup
 * Only rendered when the admin has enabled custom payment for the trainer.
 */
export default function CoursePaymentMethods({ value, onChange, capabilities }: CoursePaymentMethodsProps) {
  const { t } = useTranslation('teaching');
  const ownStripeStatus = capabilities.ownStripeStatus;
  const ownStripeNotReady = ownStripeStatus !== 'ready';
  const stripeUsable = value.stripeEnabled && (value.paymentProvider === 'platform' || !ownStripeNotReady);
  const qrUsable = value.qrPaymentEnabled && capabilities.hasQrMethods;
  const noMethod = value.price > 0 && !stripeUsable && !qrUsable;

  return (
    <div className="border-t border-gray-200 pt-4">
      <div className="flex items-center gap-2 mb-1">
        <CreditCard className="w-4 h-4 text-gold" />
        <span className="text-sm font-semibold text-gray-900">{t('courseEdit.payment')}</span>
      </div>
      <p className="text-xs text-gray-500 mb-3">{t('courseEdit.paymentMethodsHint')}</p>

      {/* Stripe card checkout: Default (platform) or the trainer's own account */}
      <label className="flex items-center gap-3 cursor-pointer mb-2">
        <input type="checkbox" checked={value.stripeEnabled} onChange={(e) => onChange({ stripeEnabled: e.target.checked })} className={checkboxCls} />
        <CreditCard className="w-4 h-4 text-gray-500" />
        <span className="text-sm text-gray-900">{t('courseEdit.enableStripePayments')}</span>
      </label>
      {value.stripeEnabled && (
        <div className="pl-7 space-y-2 mb-3">
          <label className="flex items-center gap-2 cursor-pointer">
            <input type="radio" name="paymentProvider" value="platform" checked={value.paymentProvider === 'platform'} onChange={() => onChange({ paymentProvider: 'platform' })} className={radioCls} />
            <span className="text-sm text-gray-900">{t('courseEdit.platformStripe')}</span>
          </label>
          <label className="flex items-center gap-2 cursor-pointer">
            <input type="radio" name="paymentProvider" value="custom" checked={value.paymentProvider === 'custom'} onChange={() => onChange({ paymentProvider: 'custom' })} className={radioCls} />
            <span className="text-sm text-gray-900">{t('courseEdit.myStripeAccount')}</span>
            {ownStripeStatus !== 'ready' && (
              <span className={`text-xs ${ownStripeStatus === 'unreadable' ? 'text-red-600' : 'text-amber-600'}`}>
                ({t(OWN_STRIPE_STATUS_KEY[ownStripeStatus])})
              </span>
            )}
          </label>
          {value.paymentProvider === 'custom' && ownStripeNotReady && (
            <p className="pl-6 text-xs text-amber-600">
              {t('courseEdit.ownStripeNotReadyHint')}{' '}
              <Link to={PAYMENT_CONFIG_PATH} className="underline hover:text-amber-700">{t('courseEdit.openPaymentConfig')}</Link>
            </p>
          )}
        </div>
      )}

      {/* Customized payment: popup with the trainer's WeChat / Alipay / custom instructions */}
      <label className="flex items-center gap-3 cursor-pointer mb-1">
        <input type="checkbox" checked={value.qrPaymentEnabled} onChange={(e) => onChange({ qrPaymentEnabled: e.target.checked })} className={checkboxCls} />
        <QrCode className="w-4 h-4 text-gray-500" />
        <span className="text-sm text-gray-900">{t('courseEdit.enableQrPayments')}</span>
      </label>
      {value.qrPaymentEnabled && (
        <p className="pl-7 text-xs mb-1">
          {capabilities.hasQrMethods ? (
            <span className="text-gray-500">{t('courseEdit.qrPaymentsUsesConfig')}</span>
          ) : (
            <span className="text-amber-600">
              {t('courseEdit.qrNotConfigured')}{' '}
              <Link to={PAYMENT_CONFIG_PATH} className="underline hover:text-amber-700">{t('courseEdit.setUpQr')}</Link>
            </span>
          )}
        </p>
      )}

      {noMethod && (
        <div className="mt-3 flex items-start gap-2 rounded-md bg-amber-50 border border-amber-200 p-2 text-xs text-amber-700">
          <AlertTriangle className="w-4 h-4 flex-shrink-0" />
          <span>{t('courseEdit.noPaymentMethodWarning')}</span>
        </div>
      )}
    </div>
  );
}
